import type { IntegrationProviderType } from '@/api/integration';
import type { UserType } from '@/api/user';
import type { RuleBuilderRulesType } from '@/features/RuleBuilder';

import { type CompanyDetailType } from '../activity';
import { type BaseMetadataType, type OptionType } from '../common';
import { type Edge, type SolDeleteResponseType } from '../solType';

type AudienceTypeType = 'salesforce' | 'hubspot' | 'upload' | 'rule' | 'lookalike';

export type LookalikeProfileType = 'crm' | 'custom';

export const AudienceJobStatus = {
  /**
   * @deprecated Use PROCESSING instead
   */
  QUEUED: 'queued',
  PROCESSING: 'processing',
  ERROR: 'error',
  COMPLETED: 'completed',
  UNKNOWN: 'unknown',
} as const;

export type AudienceJobStatus = (typeof AudienceJobStatus)[keyof typeof AudienceJobStatus];

export type AudienceJobType = {
  status: AudienceJobStatus;
  errors?: string | null;
  created_at: string;
};

export type AudienceActivationType = {
  provider: IntegrationProviderType;
  activatedAt: string;
  activatedBy: UserType;
  lastActivationJob?: AudienceJobType;
};

export type AudienceType = BaseMetadataType & {
  type: AudienceTypeType;
  activations?: AudienceActivationType[];
  metrics: {
    emails: number;
    ips: number;
    members: number;
    size: number;
    visits: number;
  };
  lastJob?: AudienceJobType;
  isTAM?: boolean;
  nameIsImmutable?: boolean;
  salesforce_url?: string;
  hubspot_url?: string;
  rulesConfig?: {
    rules: RuleBuilderRulesType;
    version: '1.0.0';
  };
  lookalikeConfig?: {
    audienceId: string;
    includeSampleCompanies: boolean;
    profile: LookalikeProfileType;
  };
  dataset_id?: string;
};

export type AudienceDatasetResponse = {
  dataset: string;
  rows: {
    error: number;
    success: number;
  };
};

export type AudienceListGQLResponse = {
  audiences: {
    get: {
      edges: Edge<AudienceType>[];
      totalEdges: number;
    };
  };
};

export type AudienceGQLResponse = {
  audiences: {
    getById: Omit<AudienceType, 'rulesConfig'> & {
      rulesConfig?: string;
    };
  };
};

export type AudienceRulesPreviewGQLResponse = {
  audiences: {
    getPreview: {
      audienceVisits: number;
      audienceEngagedCompanies: number;
      totalEngagedCompanies: number;
      totalVisits: number;
    };
  };
};

export type AudienceCreateType = Omit<
  AudienceType,
  'id' | 'created_at' | 'created_by' | 'metrics' | 'rulesConfig'
> & {
  rulesConfig?: string;
};

export type AudienceUpdateType = Omit<
  AudienceType,
  'created_at' | 'created_by' | 'metrics' | 'type'
> & {
  rulesConfig?: string;
};

export type AudienceCreateVariablesType = {
  audience: Omit<AudienceCreateType, 'isTAM'> & { is_default?: boolean };
};

export type AudienceUpdateVariablesType = {
  audience: AudienceUpdateType;
};

export type AudienceCreateResponseType = {
  audiences: {
    create: AudienceType;
  };
};

export type AudienceUpdateResponseType = {
  audiences: {
    update: AudienceType;
  };
};

export type AudienceDeleteResponseType = {
  audiences: SolDeleteResponseType;
};

export type AudienceActivateVariablesType = {
  audienceId: string;
  providerIds: string[];
};

export type AudienceDeactivateVariablesType = {
  audienceId: string;
  providerIds: string[];
};

export type AudienceGroupedOptionType = AudienceOptionType & {
  audienceIds: string[];
};

export type AudienceOptionType = OptionType & {
  metrics?: {
    size: number;
  };
  isTAM?: boolean;
  lastJob?: {
    status: AudienceJobStatus;
  };
};

export type AudienceOptionGQLResponseType = {
  audiences: {
    get: {
      edges: {
        node: AudienceOptionType;
      }[];
      pageMeta: {
        hasNext: boolean;
      };
    };
  };
};

export type AudienceMembershipGQLResponse = {
  companies: {
    getByAudienceId: {
      edges: Edge<CompanyDetailType>[];
      totalEdges: number;
    };
  };
};

export type AudienceOptionByIdGQLResponseType = {
  audiences: {
    getById: AudienceOptionType;
  };
};

export type SalesforceAudienceReportMetadataGQLResponse = {
  audiences: {
    getAudienceReportMetadata: {
      name?: string;
    };
  };
};
